import {Spec} from 'immutability-helper';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ClientId} from 'notebook/ELNConstants';
import {copyNoteLinkedScratchPlateDesignForPaste} from 'notebook/integrations/plate-maps/scratch-plates/NoteLinkedScratchPlateDesignUtil';
import ScratchPlateDesignSummary from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummary';
import {isScratchPlateDesignAvailableForDocument} from 'notebook/integrations/plate-maps/scratch-plates/worksheetIntegrationGating';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {GenericNoteLinkedScratchPlateDesign} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';

import {generatePlateMapCollapseId, PLATE_MAP_PLUGIN_VIRTUALIZED_HEIGHT} from '../PlateMapPluginUtils';

type NoteLinkedScratchPlateDesign = GenericNoteLinkedScratchPlateDesign<ClientId>;
export type ScratchPlateDesignNotePart = NotePart & {item: NoteLinkedScratchPlateDesign};

interface ScratchPlateDesignItemProps {}

const NOTE_LINKED_SCRATCH_PLATE_DESIGN_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.NOTE_LINKED_SCRATCH_PLATE_DESIGN,
  title: 'Plate Design',
  icon: 'tube-arrow-right',
  linkable: false,
  alignable: false,
  canPasteFromOtherTenant: false,
  isCollapsible: () => true,
  canAutoCollapse: false,
  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: [],
    getItemProps(this: NoteEditor, _notePart: ScratchPlateDesignNotePart): ScratchPlateDesignItemProps {
      return {};
    },
    make(
      this: NoteEditor,
      notePart: ScratchPlateDesignNotePart,
      index: number,
      editorProps: NoteEditorProps,
      _itemProps: ScratchPlateDesignItemProps,
      _searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const collapseId = generatePlateMapCollapseId(
        this.props.fileId,
        notePart.item.type,
        notePart.provenanceId
      );
      return (
        <SelectableNotePart indentation={notePart.indentation}>
          <ScratchPlateDesignSummary
            scratchPlateId={String(
              (notePart.item.data.scratchPlateCid !== undefined
                ? editorProps.getIdFromCid(notePart.item.data.scratchPlateCid)
                : undefined) ?? notePart.item.data.scratchPlateId
            )}
            locked={editorProps.locked}
            viewer={editorProps.viewer || !isScratchPlateDesignAvailableForDocument(this.props.fileId)}
            collapseId={collapseId}
            isEntryTemplate={APIUtil.isEntryTemplate(this.props.fileId)}
            onEdit={(initialState, scratchPlateId, refetch) => {
              this._openScratchPlateDesignEditModal(
                index,
                notePart.item.cid,
                scratchPlateId,
                initialState,
                refetch
              );
            }}
            onFillPlates={(initialDesignState, originDesignApiId, refetch) => {
              this._openFillPlatesModal(
                editorProps.getCid(notePart),
                initialDesignState,
                originDesignApiId,
                refetch
              );
            }}
          />
        </SelectableNotePart>
      );
    },
    getVirtualizedHeight: (_item: ScratchPlateDesignNotePart, _isCollapsed: boolean) =>
      PLATE_MAP_PLUGIN_VIRTUALIZED_HEIGHT,
  },
  itemToText(this: NoteEditor, item: ScratchPlateDesignNotePart): string {
    return item.item.data.scratchPlateName || item.item.data.scratchPlateId;
  },
  itemToHTML(this: NoteEditor, $el: JQuery, _item: ScratchPlateDesignNotePart): JQuery {
    return $el;
  },
  itemFromHTML(
    this: NoteEditor,
    _$el: JQuery,
    notePart: ScratchPlateDesignNotePart
  ): ScratchPlateDesignNotePart | null {
    if (!isScratchPlateDesignAvailableForDocument(this.props.fileId)) {
      notifications.warn('Plate designs are not currently supported in this context.', {
        id: 'xdoc-scratch-plate-design-paste',
      });
      return null;
    }

    return {
      ...notePart,
      item: copyNoteLinkedScratchPlateDesignForPaste(notePart.item),
    };
  },
  getContent(this: NoteEditor, {item}: ScratchPlateDesignNotePart): {item: NoteLinkedScratchPlateDesign} {
    return {item};
  },
  setContent(this: NoteEditor, _data: unknown): Spec<NotePart> {
    throw new Error('Cannot set content for non-registry plate designs.');
  },
  getReasonForDisallowedAction(
    this: NoteEditor,
    _item: ScratchPlateDesignNotePart,
    _action: MediocreItemAction
  ): string | null {
    return null;
  },
};

export default NOTE_LINKED_SCRATCH_PLATE_DESIGN_PLUGIN;
